import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Upload, X, CheckCircle2, FileText, Loader2 } from "lucide-react";
import { Layout } from "@/components/site/Layout";
import { useSEO } from "@/hooks/useSEO";
import { DIVISIONS } from "@/data/catalog";
import { api } from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";

const PROJECT_TYPES = DIVISIONS.map((d) => ({ key: d.projectCategory, label: d.title, slug: d.slug, services: d.services }));
PROJECT_TYPES.push({ key: "Interior Design", label: "Interior Design", slug: "interior-design", services: ["Residential Interior Design", "Commercial Interior Design", "Space Planning", "Concept Design", "3D Visualisation", "Material & Finish Selection"] });

const STEPS = ["Project Type", "Work Required", "Project Details", "Files", "Contact", "Confirm"];

export default function Quotation() {
  useSEO({ title: "Request a Quotation", description: "Request a renovation, fit-out, interior design or metal fabrication quotation from WAAN METAL in Singapore." });
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    project_type: "", services: [], property_type: "", floor_area: "", location: "",
    description: "", commencement_date: "", budget: "", file_ids: [], files: [],
    name: "", phone: "", email: "", preferred_contact: "Phone", consent: false,
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);

  const selectedType = useMemo(() => PROJECT_TYPES.find((t) => t.key === data.project_type), [data.project_type]);

  const set = (patch) => setData((d) => ({ ...d, ...patch }));
  const toggleService = (s) => set({ services: data.services.includes(s) ? data.services.filter((x) => x !== s) : [...data.services, s] });

  const canNext = () => {
    if (step === 0) return !!data.project_type;
    if (step === 1) return data.services.length > 0;
    if (step === 4) return data.name && data.phone && data.email && data.consent;
    return true;
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name} exceeds 10MB`); continue; }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data: res } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        set({ file_ids: [...data.file_ids, res.file_id], files: [...data.files, { id: res.file_id, name: res.filename }] });
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || `Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    e.target.value = "";
  };

  const removeFile = (id) => set({ file_ids: data.file_ids.filter((f) => f !== id), files: data.files.filter((f) => f.id !== id) });

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data: res } = await api.post("/enquiries", {
        project_type: data.project_type, services: data.services, property_type: data.property_type,
        floor_area: data.floor_area, location: data.location, description: data.description,
        commencement_date: data.commencement_date, budget: data.budget, file_ids: data.file_ids,
        name: data.name, phone: data.phone, email: data.email, preferred_contact: data.preferred_contact, consent: data.consent,
      });
      setReference(res.reference);
      trackEvent("quotation_submit", { project_type: data.project_type, services_count: data.services.length });
      setStep(5);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const field = "w-full px-4 py-3 rounded-lg border border-slate-300 focus:border-[#B87333] focus:ring-1 focus:ring-[#B87333] outline-none";

  return (
    <Layout>
      <section className="bg-[#0A1128] py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="eyebrow mb-3">Request a Quotation</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">Tell us about your project</h1>
          <p className="mt-3 text-slate-300">Six quick steps. We'll prepare a transparent, itemised quotation.</p>
        </div>
      </section>

      <section className="py-12 lg:py-16 bg-slate-50 min-h-[60vh]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Stepper */}
          <div className="flex items-center justify-between mb-10 overflow-x-auto" data-testid="quotation-stepper">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center shrink-0">
                <div className={`flex items-center gap-2 ${i <= step ? "" : "opacity-40"}`}>
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i < step ? "bg-[#B87333] text-[#0A1128]" : i === step ? "bg-[#0A1128] text-white" : "bg-slate-200 text-slate-500"}`}>
                    {i < step ? <CheckCircle2 size={17} /> : i + 1}
                  </span>
                  <span className="hidden sm:inline text-xs font-semibold text-slate-700">{s}</span>
                </div>
                {i < STEPS.length - 1 && <span className="w-4 sm:w-8 h-px bg-slate-300 mx-1 sm:mx-2" />}
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-10">
            {/* Step 0 */}
            {step === 0 && (
              <div data-testid="quotation-step-0">
                <h2 className="text-xl font-bold text-slate-900 mb-6">What type of project is this?</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {PROJECT_TYPES.map((t) => (
                    <button key={t.key} onClick={() => set({ project_type: t.key, services: [] })} data-testid={`quotation-type-${t.slug}`}
                      className={`text-left p-5 rounded-xl border-2 transition-all ${data.project_type === t.key ? "border-[#B87333] bg-[#B87333]/5" : "border-slate-200 hover:border-slate-300"}`}>
                      <span className="font-semibold text-slate-900">{t.label}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 1 */}
            {step === 1 && (
              <div data-testid="quotation-step-1">
                <h2 className="text-xl font-bold text-slate-900 mb-2">What work is required?</h2>
                <p className="text-sm text-slate-500 mb-6">Select all that apply.</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  {(selectedType?.services || []).map((s) => (
                    <label key={s} data-testid={`quotation-service-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${data.services.includes(s) ? "border-[#B87333] bg-[#B87333]/5" : "border-slate-200 hover:border-slate-300"}`}>
                      <input type="checkbox" checked={data.services.includes(s)} onChange={() => toggleService(s)} className="accent-[#B87333] w-4 h-4" />
                      <span className="text-sm text-slate-800">{s}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {/* Step 2 */}
            {step === 2 && (
              <div data-testid="quotation-step-2" className="space-y-5">
                <h2 className="text-xl font-bold text-slate-900">Project details</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className="text-sm font-medium text-slate-700 block mb-1.5">Property type</label><input value={data.property_type} onChange={(e) => set({ property_type: e.target.value })} placeholder="e.g. 4-room HDB, Office" className={field} data-testid="quotation-property-type" /></div>
                  <div><label className="text-sm font-medium text-slate-700 block mb-1.5">Approx. floor area</label><input value={data.floor_area} onChange={(e) => set({ floor_area: e.target.value })} placeholder="e.g. 90 sqm" className={field} data-testid="quotation-floor-area" /></div>
                  <div><label className="text-sm font-medium text-slate-700 block mb-1.5">Project location</label><input value={data.location} onChange={(e) => set({ location: e.target.value })} placeholder="e.g. Tampines" className={field} data-testid="quotation-location" /></div>
                  <div><label className="text-sm font-medium text-slate-700 block mb-1.5">Estimated budget</label><input value={data.budget} onChange={(e) => set({ budget: e.target.value })} placeholder="e.g. S$30,000" className={field} data-testid="quotation-budget" /></div>
                  <div><label className="text-sm font-medium text-slate-700 block mb-1.5">Desired commencement</label><input type="date" value={data.commencement_date} onChange={(e) => set({ commencement_date: e.target.value })} className={field} data-testid="quotation-date" /></div>
                </div>
                <div><label className="text-sm font-medium text-slate-700 block mb-1.5">Description</label><textarea rows={4} value={data.description} onChange={(e) => set({ description: e.target.value })} placeholder="Describe your project scope and requirements" className={`${field} resize-none`} data-testid="quotation-description" /></div>
              </div>
            )}

            {/* Step 3 */}
            {step === 3 && (
              <div data-testid="quotation-step-3">
                <h2 className="text-xl font-bold text-slate-900 mb-2">Upload reference files</h2>
                <p className="text-sm text-slate-500 mb-6">Photos, drawings, floor plans or reference images. JPG, PNG, WEBP or PDF, up to 10MB each. (Optional)</p>
                <label className="flex flex-col items-center justify-center gap-2 p-10 rounded-xl border-2 border-dashed border-slate-300 hover:border-[#B87333] cursor-pointer transition-colors" data-testid="quotation-upload-dropzone">
                  {uploading ? <Loader2 size={28} className="text-[#B87333] animate-spin" /> : <Upload size={28} className="text-[#B87333]" />}
                  <span className="text-sm font-medium text-slate-700">{uploading ? "Uploading…" : "Click to upload files"}</span>
                  <input type="file" multiple accept=".jpg,.jpeg,.png,.webp,.gif,.pdf" className="hidden" onChange={handleUpload} data-testid="quotation-file-input" />
                </label>
                {data.files.length > 0 && (
                  <ul className="mt-5 space-y-2" data-testid="quotation-file-list">
                    {data.files.map((f) => (
                      <li key={f.id} className="flex items-center justify-between px-4 py-3 rounded-lg bg-slate-50 border border-slate-200">
                        <span className="flex items-center gap-2.5 text-sm text-slate-700"><FileText size={16} className="text-[#B87333]" /> {f.name}</span>
                        <button onClick={() => removeFile(f.id)} className="text-slate-400 hover:text-red-500" data-testid={`quotation-remove-file-${f.id}`}><X size={18} /></button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Step 4 */}
            {step === 4 && (
              <div data-testid="quotation-step-4" className="space-y-5">
                <h2 className="text-xl font-bold text-slate-900">Your contact details</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><label className="text-sm font-medium text-slate-700 block mb-1.5">Full name *</label><input value={data.name} onChange={(e) => set({ name: e.target.value })} className={field} data-testid="quotation-name" /></div>
                  <div><label className="text-sm font-medium text-slate-700 block mb-1.5">Phone *</label><input value={data.phone} onChange={(e) => set({ phone: e.target.value })} className={field} data-testid="quotation-phone" /></div>
                </div>
                <div><label className="text-sm font-medium text-slate-700 block mb-1.5">Email *</label><input type="email" value={data.email} onChange={(e) => set({ email: e.target.value })} className={field} data-testid="quotation-email" /></div>
                <div>
                  <label className="text-sm font-medium text-slate-700 block mb-1.5">Preferred contact method</label>
                  <div className="flex gap-3">
                    {["Phone", "Email", "WhatsApp"].map((m) => (
                      <button key={m} onClick={() => set({ preferred_contact: m })} data-testid={`quotation-contact-${m.toLowerCase()}`} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${data.preferred_contact === m ? "bg-[#0A1128] text-white" : "bg-slate-100 text-slate-700"}`}>{m}</button>
                    ))}
                  </div>
                </div>
                <label className="flex gap-3 text-sm text-slate-600">
                  <input type="checkbox" checked={data.consent} onChange={(e) => set({ consent: e.target.checked })} className="mt-1 accent-[#B87333]" data-testid="quotation-consent" />
                  <span>I consent to WAAN METAL collecting and using my personal data to respond to this enquiry, in line with the <Link to="/privacy" className="text-[#B87333] underline">Privacy Policy</Link>.</span>
                </label>
              </div>
            )}

            {/* Step 5 - confirmation */}
            {step === 5 && reference && (
              <div className="text-center py-6" data-testid="quotation-confirmation">
                <CheckCircle2 size={56} className="mx-auto text-[#B87333]" />
                <h2 className="mt-5 text-2xl font-bold text-slate-900">Enquiry submitted!</h2>
                <p className="mt-2 text-slate-600">Thank you — your enquiry reference is:</p>
                <p className="mt-3 inline-block px-6 py-3 rounded-xl bg-[#0A1128] text-white text-xl font-bold font-mono-label tracking-wider" data-testid="quotation-reference">{reference}</p>
                <p className="mt-5 text-slate-600 max-w-md mx-auto">Our team will review your project and be in touch via your preferred contact method. A copy has been sent to our office.</p>
                <Link to="/" className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#B87333] to-[#D97706] text-[#0A1128] font-bold">Back to Home</Link>
              </div>
            )}

            {/* Nav buttons */}
            {step < 5 && (
              <div className="mt-8 flex items-center justify-between pt-6 border-t border-slate-100">
                <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="quotation-back" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-slate-600 font-semibold hover:bg-slate-100 disabled:opacity-40 transition-colors"><ArrowLeft size={17} /> Back</button>
                {step < 4 ? (
                  <button onClick={() => canNext() && setStep((s) => s + 1)} disabled={!canNext()} data-testid="quotation-next" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-gradient-to-r from-[#B87333] to-[#D97706] text-[#0A1128] font-bold disabled:opacity-40 hover:-translate-y-0.5 transition-all">Next <ArrowRight size={17} /></button>
                ) : (
                  <button onClick={submit} disabled={!canNext() || submitting} data-testid="quotation-submit" className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-gradient-to-r from-[#B87333] to-[#D97706] text-[#0A1128] font-bold disabled:opacity-40 hover:-translate-y-0.5 transition-all">{submitting ? "Submitting…" : "Submit Enquiry"} {!submitting && <CheckCircle2 size={17} />}</button>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
